import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import QRCode from 'qrcode';
import { saveAs } from 'file-saver';
import { getBaseUrl, getAudienceUrl, getDisplayUrl, getManageUrl } from '../utils/urlHelper';

const ShareLinksModal = ({ isOpen, onClose, sessionId }) => {
  const links = {
    manage: getManageUrl(sessionId),
    audience: getAudienceUrl(sessionId),
    display: getDisplayUrl(sessionId)
  };

  const [copiedLink, setCopiedLink] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  // Generate QR code when modal opens
  useEffect(() => {
    if (isOpen && sessionId) {
      generateQRCode();
    }
  }, [isOpen, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateQRCode = async () => {
    setQrLoading(true);
    try {
      const qrDataUrl = await QRCode.toDataURL(links.audience, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(qrDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setQrLoading(false);
    }
  };

  const downloadQRCode = () => {
    if (qrCodeDataUrl) {
      const blob = dataURLToBlob(qrCodeDataUrl);
      saveAs(blob, `asktide-session-${sessionId}-qr.png`);
    }
  };

  const dataURLToBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleCopy = (linkType) => {
    navigator.clipboard.writeText(links[linkType]);
    setCopiedLink(linkType);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share Links">
      <div className="space-y-6 p-4">
        <div className="grid gap-4">
          {/* Manage Questions Link */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">Manage Questions</h3>
              <Button
                onClick={() => handleCopy('manage')}
                variant="secondary"
                size="sm"
              >
                {copiedLink === 'manage' ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-2">For moderators to manage questions</p>
            <div className="bg-white p-2 rounded border border-gray-200">
              <code className="text-sm break-all">{links.manage}</code>
            </div>
          </div>

          {/* Audience View Link */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">Audience View</h3>
              <Button
                onClick={() => handleCopy('audience')}
                variant="secondary"
                size="sm"
              >
                {copiedLink === 'audience' ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-2">For audience to view and submit questions</p>
            <div className="bg-white p-2 rounded border border-gray-200">
              <code className="text-sm break-all">{links.audience}</code>
            </div>
          </div>

          {/* QR Code Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">QR Code</h3>
              <Button
                onClick={downloadQRCode}
                variant="secondary"
                size="sm"
                disabled={!qrCodeDataUrl || qrLoading}
              >
                {qrLoading ? 'Generating...' : 'Download'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Scan to access audience view</p>
            <div className="flex justify-center">
              {qrLoading ? (
                <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-sm text-gray-500">Generating QR...</div>
                </div>
              ) : qrCodeDataUrl ? (
                <img 
                  src={qrCodeDataUrl} 
                  alt="QR Code for Audience View" 
                  className="w-32 h-32 rounded-lg border border-gray-200"
                />
              ) : (
                <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-sm text-gray-500">QR Code</div>
                </div>
              )}
            </div>
          </div>

          {/* Display View Link */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">Display View</h3>
              <Button
                onClick={() => handleCopy('display')}
                variant="secondary"
                size="sm"
              >
                {copiedLink === 'display' ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-2">For OBS/Tricaster integration</p>
            <div className="bg-white p-2 rounded border border-gray-200">
              <code className="text-sm break-all">{links.display}</code>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button onClick={onClose} variant="secondary">Close</Button>
        </div>
      </div>
    </Modal>
  );
};

export default ShareLinksModal; 